import { useEffect, useRef, useState } from "react";

import { ensureLocationManagerInited, getLocationManager } from "@/lib/telegram";

// How often we re-poll TG LocationManager. Browser geolocation streams via
// watchPosition; TG doesn't have an equivalent, so we poll at a friendly
// pace (battery-conscious, still feels live on the map).
const TG_LOCATION_POLL_MS = 2500;

// ~0.00005° ≈ 5–6m at Moscow latitude. Anything within that bucket is
// treated as "same fix" for staleness purposes — true GPS jitter sits
// well above this even when standing still.
const SAME_FIX_EPSILON = 0.00005;
const STALE_AFTER_REPEATS = 4;

export type UserPos = { lat: number; lng: number; accuracy: number };

interface Options {
  /** First fix of a locate session — the map flies there. */
  onFirstFix?: (pos: UserPos) => void;
  /** Locate button tapped while already tracking — re-centre on the user. */
  onRecenter?: (pos: UserPos) => void;
}

/**
 * The user's live position for a map: Telegram LocationManager first (with
 * a parallel browser watch for fresher fixes), plain browser geolocation as
 * the fallback. Shared by the main map and the spot picker of «Играть».
 *
 * Moved verbatim out of MapPage — the permission/poll ordering below fixes
 * real prompt loops and marker bouncing on TG clients; keep it as is.
 */
export function useUserLocation({ onFirstFix, onRecenter }: Options = {}) {
  const [userPos, setUserPos] = useState<UserPos | null>(null);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);
  // `stale` flips on when several consecutive fixes carry the same coords —
  // typically TG/OS handing us a cached `getLastKnownLocation` while the GPS
  // is still warming. Tapping the locate button while stale forces a restart.
  const [fixStale, setFixStale] = useState(false);
  const watchIdRef = useRef<number | null>(null);
  const tgPollIdRef = useRef<number | null>(null);
  const firstFixRef = useRef(true);
  // Guards against in-flight permission/getLocation callbacks resuming work
  // after stopLocating() — TG's getLocation has no cancel API, and
  // navigator.geolocation callbacks can fire one more time after clearWatch.
  const locateStoppedRef = useRef(false);
  // Remember a denial within the session so the next tap on the locate
  // button doesn't re-trigger the OS/TG permission prompt (which the user
  // has already dismissed once).
  const browserDeniedRef = useRef(false);
  // Last coords we saw + count of consecutive identical fixes; used to flip
  // `fixStale` so the locate button can offer a force-refresh action.
  const lastFixRef = useRef<{ lat: number; lng: number } | null>(null);
  const sameFixCountRef = useRef(0);
  // Callbacks change identity every render; read the latest through a ref
  // so long-lived watch/poll closures never call a stale one.
  const callbacksRef = useRef({ onFirstFix, onRecenter });
  callbacksRef.current = { onFirstFix, onRecenter };

  useEffect(() => {
    return () => {
      stopLocating();
    };
  }, []);

  const handleFix = (next: UserPos) => {
    const prev = lastFixRef.current;
    if (
      prev &&
      Math.abs(prev.lat - next.lat) < SAME_FIX_EPSILON &&
      Math.abs(prev.lng - next.lng) < SAME_FIX_EPSILON
    ) {
      sameFixCountRef.current += 1;
      if (sameFixCountRef.current >= STALE_AFTER_REPEATS) {
        setFixStale(true);
      }
    } else {
      sameFixCountRef.current = 0;
      setFixStale(false);
    }
    lastFixRef.current = { lat: next.lat, lng: next.lng };
    setUserPos(next);
    if (firstFixRef.current) {
      callbacksRef.current.onFirstFix?.(next);
      firstFixRef.current = false;
    }
  };

  /** Stop tracking. `error` survives the reset so the toast can explain why. */
  const stopLocating = (error: string | null = null) => {
    locateStoppedRef.current = true;
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (tgPollIdRef.current != null) {
      window.clearInterval(tgPollIdRef.current);
      tgPollIdRef.current = null;
    }
    setLocating(false);
    setUserPos(null);
    setLocateError(error);
    setFixStale(false);
    lastFixRef.current = null;
    sameFixCountRef.current = 0;
  };

  const startBrowserGeolocation = () => {
    if (!("geolocation" in navigator)) {
      setLocateError("Геолокация недоступна");
      return;
    }
    if (browserDeniedRef.current) {
      // Already denied once in this session — don't re-trigger the OS prompt.
      setLocateError("Нет доступа к геолокации");
      return;
    }
    setLocateError(null);
    setLocating(true);
    firstFixRef.current = true;
    locateStoppedRef.current = false;
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        if (locateStoppedRef.current) return;
        handleFix({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      (err) => {
        if (locateStoppedRef.current) return;
        if (err.code === err.PERMISSION_DENIED) {
          browserDeniedRef.current = true;
          setLocateError("Нет доступа к геолокации");
        } else {
          setLocateError("Не удалось определить местоположение");
        }
        setLocating(false);
        if (watchIdRef.current != null) {
          navigator.geolocation.clearWatch(watchIdRef.current);
          watchIdRef.current = null;
        }
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
  };

  /**
   * TG LocationManager flow: request a fresh fix once; if granted, start
   * polling. We deliberately do NOT start setInterval before the first
   * callback resolves — otherwise the timer fires more getLocation() calls
   * while TG's permission dialog is still on screen, each of which can
   * re-trigger the prompt and produce a loop on refusal.
   */
  const startTelegramGeolocation = async (lm: TelegramLocationManager) => {
    // If the user already refused once this session, don't ask again —
    // TG re-shows the permission sheet on every getLocation() call when
    // access is not granted.
    if (lm.isAccessRequested && !lm.isAccessGranted) {
      setLocateError("Нет доступа к геолокации в Telegram");
      return;
    }
    setLocateError(null);
    setLocating(true);
    firstFixRef.current = true;
    locateStoppedRef.current = false;

    const handleData = (data: TelegramLocationData | null) => {
      if (locateStoppedRef.current) return;
      if (data) {
        handleFix({
          lat: data.latitude,
          lng: data.longitude,
          accuracy: data.horizontal_accuracy ?? 30,
        });
        return;
      }
      // No data → permission denied or location off. Stop, don't retry.
      // (The message used to be set and then wiped by stopLocating().)
      stopLocating(
        lm.isAccessRequested
          ? "Нет доступа к геолокации в Telegram"
          : "Доступ к геолокации не предоставлен",
      );
    };

    const pollOnce = () => {
      if (locateStoppedRef.current) return;
      // Defensive: if access got revoked between polls, bail out instead
      // of calling getLocation (which would re-prompt).
      if (lm.isAccessRequested && !lm.isAccessGranted) {
        stopLocating("Нет доступа к геолокации в Telegram");
        return;
      }
      lm.getLocation(handleData);
    };

    // First call — wait for it before scheduling further polls.
    lm.getLocation((data) => {
      handleData(data);
      if (locateStoppedRef.current || !data) return;
      // Some TG clients (especially Android) expose navigator.geolocation
      // inside the WebView and stream real-time fixes faster than our 2.5s
      // TG poll. Run it in parallel — if it works, we get freshness; if the
      // WebView blocks it, the error callback fires silently and we keep
      // relying on TG polling. Both paths feed the same `handleFix`, so the
      // marker just gets the freshest of the two.
      startTgParallelBrowserWatch();
      tgPollIdRef.current = window.setInterval(pollOnce, TG_LOCATION_POLL_MS);
    });
  };

  /** Best-effort: spin up navigator.geolocation alongside TG polling.
   *  As soon as the browser stream produces a usable fix we shut down the
   *  TG poll — running both produces visible jitter because TG keeps
   *  returning a cached point while the browser streams live ones, and
   *  the marker bounces between them. */
  const startTgParallelBrowserWatch = () => {
    if (!("geolocation" in navigator)) return;
    if (watchIdRef.current != null) return;
    try {
      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          if (locateStoppedRef.current) return;
          // First usable browser fix wins — kill the TG poll so the two
          // sources stop fighting. "Usable" = reasonable accuracy; a 1km
          // IP-based fix shouldn't dethrone a 30m TG fix.
          if (
            tgPollIdRef.current != null &&
            pos.coords.accuracy != null &&
            pos.coords.accuracy <= 100
          ) {
            window.clearInterval(tgPollIdRef.current);
            tgPollIdRef.current = null;
          }
          handleFix({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          });
        },
        () => {
          // Silent: WebView likely blocked navigator.geolocation. Falling
          // back entirely to TG polling. Do NOT setLocateError here — the
          // TG path is still healthy.
          if (watchIdRef.current != null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
          }
        },
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
      );
    } catch {
      // Some WebViews throw synchronously on watchPosition — ignore.
    }
  };

  const startLocating = async () => {
    const lm = getLocationManager();
    if (lm) {
      const ready = await ensureLocationManagerInited();
      if (ready) {
        if (!ready.isLocationAvailable) {
          setLocateError("Геолокация недоступна на этом устройстве");
          return;
        }
        // Short-circuit if user has already refused — going further would
        // re-trigger TG's permission sheet.
        if (ready.isAccessRequested && !ready.isAccessGranted) {
          setLocateError("Нет доступа к геолокации в Telegram");
          return;
        }
        void startTelegramGeolocation(ready);
        return;
      }
      // init failed (timeout) — fall through to browser API.
    }
    startBrowserGeolocation();
  };

  const handleLocateClick = () => {
    if (locating) {
      // Same fix coming back for ~10s = TG/OS is handing us cached GPS.
      // Tap forces a clean restart, which on most clients pushes the
      // platform to reissue a real query and wake up the GPS sensor.
      if (fixStale) {
        stopLocating();
        // Wait a tick so locateStoppedRef and refs settle before re-init.
        window.setTimeout(() => startLocating(), 0);
        return;
      }
      if (userPos) {
        callbacksRef.current.onRecenter?.(userPos);
      } else {
        stopLocating();
      }
    } else {
      startLocating();
    }
  };

  return {
    userPos,
    locating,
    locateError,
    fixStale,
    dismissError: () => setLocateError(null),
    handleLocateClick,
  };
}
